/**
 * FactCheckTab — the primary view in v1.
 *
 * Flow:
 *   1. User selects text → "Run Fact Check" button appears
 *   2. Button click → POST /v1/extract → poll until done → build claim cards
 *   3. POST /v1/validate once with all claims (single job, one source config)
 *   4. Poll until the job completes — apply each partial result as it arrives
 *   5. SummaryBar counts tick up as individual claims finish validating
 *
 * State machine: idle → extracting → validating → done | error
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Alert, AlertDescription, Label } from '@e14c/feathers';
import { Download, Renew } from '@e14c/feathers/icons';

import { useFactCheckerStore } from '../store';
import { useWordSelection } from '../hooks/useWordSelection';
import { SummaryBar } from '../components/SummaryBar';
import { ClaimCard } from '../components/ClaimCard';

import {
  startExtraction,
  getExtractionJob,
  startValidation,
  getValidationJob,
  pollJob,
  PAPER_INDEX_TOP_K,
  type ValidationSource,
} from '../api/factChecker';
import { getUserSettings, listPaperIndexes } from '../api/contentGen';
import type {
  ClaimCard as ClaimCardType,
  PaperIndexOut,
  ValidationResultOut,
  Verdict,
} from '../types';
import { useTabLayoutStyles } from '../styles/tabLayout';
import { TabScrollArea } from '../components/TabScrollArea';
import { downloadFactCheckResultsCsv } from '../utils/csvExport';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const FactCheckTab: React.FC = () => {
  const layout = useTabLayoutStyles();

  const selectedText = useFactCheckerStore((s) => s.selectedText);
  const phase = useFactCheckerStore((s) => s.phase);
  const claims = useFactCheckerStore((s) => s.claims);
  const verdictFilter = useFactCheckerStore((s) => s.verdictFilter);
  const globalError = useFactCheckerStore((s) => s.globalError);
  const summary = useFactCheckerStore((s) => s.summary);
  const setPhase = useFactCheckerStore((s) => s.setPhase);
  const setExtractionJobId = useFactCheckerStore((s) => s.setExtractionJobId);
  const setValidationJobId = useFactCheckerStore((s) => s.setValidationJobId);
  const setClaims = useFactCheckerStore((s) => s.setClaims);
  const setGlobalError = useFactCheckerStore((s) => s.setGlobalError);
  const resolveAllClaimValidations = useFactCheckerStore(
    (s) => s.resolveAllClaimValidations,
  );
  const mergeValidationResults = useFactCheckerStore(
    (s) => s.mergeValidationResults,
  );
  const toggleVerdictFilter = useFactCheckerStore((s) => s.toggleVerdictFilter);
  const reset = useFactCheckerStore((s) => s.reset);
  const setSelectedText = useFactCheckerStore((s) => s.setSelectedText);

  const [validationMethod, setValidationMethod] = useState<
    'web_search' | 'paper_index'
  >('web_search');
  const [paperIndexes, setPaperIndexes] = useState<PaperIndexOut[]>([]);
  const [selectedIndexId, setSelectedIndexId] = useState<string | null>(null);
  const [indexesLoaded, setIndexesLoaded] = useState(false);

  // Load the selectable literature indexes and the user's saved default. The
  // default (from Manage → Literature Index) pre-selects the dropdown, but the
  // user can pick a different index per run. The two calls are independent so
  // the list still populates if the saved-default lookup is unavailable.
  useEffect(() => {
    let cancelled = false;

    void listPaperIndexes()
      .then((indexes) => {
        if (cancelled) return;
        setPaperIndexes(indexes);
        setSelectedIndexId((current) => current ?? indexes[0]?.id ?? null);
      })
      .catch(() => {
        // Index list unavailable — web search still works.
      })
      .finally(() => {
        if (!cancelled) setIndexesLoaded(true);
      });

    void getUserSettings()
      .then((settings) => {
        if (cancelled || !settings.default_paper_index_id) return;
        setSelectedIndexId(settings.default_paper_index_id);
      })
      .catch(() => {
        // Saved default unavailable — fall back to the first index.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useWordSelection({ setSelectedText, refreshTrigger: phase });

  const runFactCheck = useCallback(async () => {
    if (!selectedText) return;

    const sources: ValidationSource[] =
      validationMethod === 'paper_index'
        ? [
            {
              type: 'paper_index',
              index_id: selectedIndexId!,
              top_k: PAPER_INDEX_TOP_K,
            },
          ]
        : [{ type: 'web_search' }];

    if (validationMethod === 'paper_index' && !selectedIndexId) {
      setGlobalError(
        'Select a literature index before running index validation.',
      );
      setPhase('error');
      return;
    }

    const runId = useFactCheckerStore.getState().validationRunId + 1;
    useFactCheckerStore.setState({ validationRunId: runId });

    try {
      setPhase('extracting');
      setGlobalError(null);
      useFactCheckerStore.setState({ verdictFilter: null });

      const extractResp = await startExtraction({ text: selectedText });
      if (!extractResp.data) {
        throw new Error(
          extractResp.error?.message ?? 'Failed to start extraction',
        );
      }

      const extractJobId = extractResp.data.job_id;
      setExtractionJobId(extractJobId);

      const extractResult = await pollJob(() =>
        getExtractionJob(extractJobId),
      );

      if (runId !== useFactCheckerStore.getState().validationRunId) return;

      const allClaims: ClaimCardType[] = (
        extractResult.results?.sentences ?? []
      ).flatMap((sentence) =>
        sentence.claims.map((c) => ({
          key: c.claim_id,
          claim_id: c.claim_id,
          text: c.text,
          sentence_position: sentence.position,
          validationStatus: 'pending' as const,
          verdict: null,
          confidence: null,
          reasoning: null,
          supporting_chunks: [],
          contradicting_chunks: [],
          source_types_used: [],
        })),
      );

      if (allClaims.length === 0) {
        setClaims([]);
        setPhase('done');
        return;
      }

      setClaims(allClaims);
      setPhase('validating');

      const claimRefs = allClaims.map((claim) =>
        claim.claim_id
          ? { claim_id: claim.claim_id }
          : { text: claim.text },
      );

      const validateResp = await startValidation({
        claims: claimRefs,
        sources,
      });

      if (!validateResp.data) {
        throw new Error(
          validateResp.error?.message ?? 'Failed to start validation',
        );
      }

      setValidationJobId(validateResp.data.job_id);

      const mapValidationResults = (results: ValidationResultOut[]) =>
        results.map((r) => ({
          position: r.position,
          verdict: r.verdict,
          confidence: r.confidence,
          reasoning: r.reasoning,
          supporting_chunks: r.supporting_chunks,
          contradicting_chunks: r.contradicting_chunks,
          source_types_used: r.source_types_used,
        }));

      const validateResult = await pollJob(
        () => getValidationJob(validateResp.data!.job_id),
        (data) => {
          if (runId !== useFactCheckerStore.getState().validationRunId) return;
          if (data.results && data.results.length > 0) {
            mergeValidationResults(runId, mapValidationResults(data.results));
          }
        },
      );

      if (runId !== useFactCheckerStore.getState().validationRunId) return;

      const results = validateResult.results ?? [];
      if (results.length === 0) {
        throw new Error('No validation results returned');
      }

      resolveAllClaimValidations(runId, mapValidationResults(results));
      setPhase('done');
    } catch (err: unknown) {
      if (runId !== useFactCheckerStore.getState().validationRunId) return;
      const message = err instanceof Error ? err.message : String(err);
      setGlobalError(message);
      setPhase('error');
    }
  }, [
    selectedText,
    validationMethod,
    selectedIndexId,
    setPhase,
    setGlobalError,
    setExtractionJobId,
    setValidationJobId,
    setClaims,
    resolveAllClaimValidations,
    mergeValidationResults,
  ]);

  const hasClaims = claims.length > 0;
  const summaryData = summary();
  const visibleClaims =
    verdictFilter === null
      ? claims
      : claims.filter(
          (claim) =>
            claim.validationStatus === 'done' && claim.verdict === verdictFilter,
        );

  const verdictFilterLabel: Record<Verdict, string> = {
    supported: 'Supported',
    unsupported: 'Unsupported',
    contradicted: 'Contradicted',
  };

  const validationMethodLabel =
    validationMethod === 'paper_index' ? 'Literature index' : 'Web search';

  const completedClaims = claims.filter(
    (claim) => claim.validationStatus === 'done',
  );
  const canDownloadCsv = completedClaims.length > 0;

  const handleDownloadCsv = useCallback(() => {
    downloadFactCheckResultsCsv(claims);
  }, [claims]);

  // ── Nothing selected ──────────────────────────────────────────────────────
  if (phase === 'idle' && !selectedText) {
    return (
      <div className={layout.panel}>
        <div className={layout.centered}>
          <span className="text-5xl leading-none">🔍</span>
          <p className="text-base font-semibold text-foreground/70">
            Select text to fact-check
          </p>
          <p className="text-sm text-muted-foreground">
            Highlight a sentence, paragraph, or section in your Word document,
            then click Run Fact Check.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={layout.panel}>
      {phase === 'done' && !hasClaims ? (
        <div className={layout.centered}>
          <span className="text-5xl leading-none">✅</span>
          <p className="text-sm">No verifiable claims found in the selected text.</p>
          <Button variant="ghost" size="sm" onClick={reset}>
            Select different text
          </Button>
        </div>
      ) : (
        <TabScrollArea>
          {/* Error banner */}
          {phase === 'error' && globalError && (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>{globalError}</span>
                <Button variant="ghost" size="sm" onClick={reset}>
                  Reset
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Selected text preview */}
          {selectedText && (phase === 'idle' || phase === 'error') && (
            <div className="bg-muted/50 border border-border rounded-md px-3 py-2">
              <p className="text-xs text-muted-foreground mb-1">Selected text</p>
              <p className="text-sm text-foreground line-clamp-3 leading-snug">
                {selectedText}
              </p>
            </div>
          )}

          {/* Validation method */}
          {(phase === 'idle' || phase === 'error') && selectedText && (
            <div className="space-y-1.5">
              <Label className="text-xs">Validation method</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                value={validationMethod}
                onChange={(e) =>
                  setValidationMethod(
                    e.target.value as 'web_search' | 'paper_index',
                  )
                }
              >
                <option value="web_search">Web search</option>
                <option value="paper_index">Literature index</option>
              </select>
              {validationMethod === 'paper_index' && (
                <div className="space-y-1.5 pt-1.5">
                  <Label className="text-xs">Literature index</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
                    value={selectedIndexId ?? ''}
                    disabled={paperIndexes.length === 0}
                    onChange={(e) => setSelectedIndexId(e.target.value || null)}
                  >
                    {paperIndexes.length === 0 ? (
                      <option value="">No indexes available</option>
                    ) : (
                      paperIndexes.map((idx) => (
                        <option key={idx.id} value={idx.id}>
                          {idx.name}
                        </option>
                      ))
                    )}
                  </select>
                  {indexesLoaded && paperIndexes.length === 0 && (
                    <p className="text-xs text-destructive">
                      No literature indexes found. Create one in the platform or
                      check that content generation is reachable.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Run button */}
          {(phase === 'idle' || phase === 'error') && selectedText && (
            <Button className="w-full" onClick={runFactCheck}>
              Run Fact Check
            </Button>
          )}

          {/* Extracting */}
          {phase === 'extracting' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="inline-block size-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
              Extracting claims…
            </div>
          )}

          {/* Summary + claims */}
          {hasClaims && (
            <>
              <SummaryBar
                summary={summaryData}
                totalClaims={claims.length}
                isValidating={phase === 'validating'}
                activeFilter={verdictFilter}
                onVerdictFilter={toggleVerdictFilter}
              />

              {verdictFilter !== null && (
                <p className="text-xs text-muted-foreground text-center px-1">
                  Showing {verdictFilterLabel[verdictFilter].toLowerCase()} claims
                  only ({visibleClaims.length} of {claims.length}) · click the
                  count again to show all
                </p>
              )}

              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <span className="text-xs text-muted-foreground">
                    {claims.length} claim{claims.length !== 1 ? 's' : ''} extracted
                  </span>
                  {(phase === 'validating' || phase === 'done') && (
                    <p className="text-xs text-muted-foreground truncate">
                      {phase === 'done'
                        ? `Validated via ${validationMethodLabel.toLowerCase()}`
                        : `Validating via ${validationMethodLabel.toLowerCase()}…`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {canDownloadCsv && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDownloadCsv}
                      aria-label="Download fact-check results as CSV"
                    >
                      <Download size={16} />
                      CSV
                    </Button>
                  )}
                  {(phase === 'done' || phase === 'error') && (
                    <Button variant="ghost" size="sm" onClick={reset}>
                      <Renew size={16} />
                      New check
                    </Button>
                  )}
                </div>
              </div>

              {phase === 'validating' && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="inline-block size-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                  Validating claims against {validationMethodLabel.toLowerCase()}…
                </div>
              )}

              {visibleClaims.map((claim) => (
                <ClaimCard key={claim.key} claim={claim} />
              ))}
            </>
          )}
        </TabScrollArea>
      )}
    </div>
  );
};
